// src/pages/RadiusPage.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { radius } from '../utils/api';
import Pagination, { usePagination } from '../components/Pagination';

export default function RadiusPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('status');
  const [testMac, setTestMac] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [newNas, setNewNas] = useState({ nasname: '', shortname: '', secret: '', type: 'cisco', description: '' });

  const { paginated: acctPage,    page: acctP,    setPage: setAcctP,    totalPages: acctTP    } = usePagination(accounting || [], 10);
  const { paginated: postauthPage, page: postP,    setPage: setPostP,    totalPages: postTP    } = usePagination(postauth   || [], 10);

  const { data: status } = useQuery({ queryKey: ['radiusStatus'], queryFn: () => radius.status().then(r => r.data.data), enabled: tab === 'status' });
  const { data: accounting } = useQuery({ queryKey: ['accounting'], queryFn: () => radius.accounting().then(r => r.data.data), enabled: tab === 'accounting' });
  const { data: postauth } = useQuery({ queryKey: ['postauth'], queryFn: () => radius.postauth().then(r => r.data.data), enabled: tab === 'postauth' });

  const syncMut = useMutation({
    mutationFn: () => radius.sync(),
    onSuccess: res => { toast.success(`Sync complete: ${res.data.data.accepted} accepted, ${res.data.data.removed} removed`); qc.invalidateQueries(['radiusStatus']); },
    onError: () => toast.error('Sync failed')
  });

  const deleteNasMut = useMutation({
    mutationFn: id => radius.deleteNas(id),
    onSuccess: () => { toast.success('NAS deleted'); qc.invalidateQueries(['radiusStatus']); }
  });

  const addNasMut = useMutation({
    mutationFn: data => radius.createNas(data),
    onSuccess: () => { toast.success('NAS added'); qc.invalidateQueries(['radiusStatus']); setNewNas({ nasname: '', shortname: '', secret: '', type: 'cisco', description: '' }); }
  });

  const handleTest = async () => {
    if (!testMac) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await radius.authTest(testMac);
      setTestResult(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Test failed');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">📡 FreeRADIUS</h1>
        <button className="btn btn-primary"
          onClick={() => { if (window.confirm('Sync all devices to FreeRADIUS now? This will push every device\'s current enabled/disabled state to the radcheck table.')) syncMut.mutate(); }}
          disabled={syncMut.isPending}>
          {syncMut.isPending ? 'Syncing…' : '⟳ Sync All Devices'}
        </button>
      </div>

      {/* Summary cards */}
      {status && (
        <div className="stat-grid" style={{ marginBottom: 24 }}>
          <div className="stat-card" style={{ '--accent': '#00e676' }}>
            <div className="stat-label">Active Accepts</div>
            <div className="stat-value" style={{ color: '#00e676' }}>{status.activeAccepts}</div>
            <div className="stat-sub">MACs allowed on network</div>
          </div>
          <div className="stat-card" style={{ '--accent': '#7c83fd' }}>
            <div className="stat-label">Total Radcheck Entries</div>
            <div className="stat-value" style={{ color: '#7c83fd' }}>{status.totalRadcheckEntries}</div>
          </div>
          <div className="stat-card" style={{ '--accent': '#ffab40' }}>
            <div className="stat-label">NAS Clients</div>
            <div className="stat-value" style={{ color: '#ffab40' }}>{status.nasList?.length || 0}</div>
            <div className="stat-sub">Wireless controllers</div>
          </div>
        </div>
      )}

      <div className="tabs">
        {['status','auth-test','nas','accounting','postauth'].map(t => (
          <div key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'status' ? 'NAS Clients' : t === 'auth-test' ? 'Auth Test' : t === 'nas' ? 'Add NAS' : t === 'accounting' ? 'Accounting' : 'Post-Auth Log'}
          </div>
        ))}
      </div>

      {/* NAS Clients */}
      {tab === 'status' && status && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">NAS / Wireless Controller Clients</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>NAS Name / IP</th><th>Short Name</th><th>Type</th><th>Description</th><th>Action</th></tr></thead>
              <tbody>
                {status.nasList.map(n => (
                  <tr key={n.id}>
                    <td className="text-mono">{n.nasname}</td>
                    <td>{n.shortname}</td>
                    <td>{n.type}</td>
                    <td>{n.description}</td>
                    <td>
                      <button className="btn btn-danger btn-sm" onClick={() => { if (window.confirm('Delete NAS?')) deleteNasMut.mutate(n.id); }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Auth Test */}
      {tab === 'auth-test' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <div className="card-header"><span className="card-title">MAC Authentication Test</span></div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 16 }}>
            Simulate how FreeRADIUS would respond to a MAC address authentication request.
          </p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <input className="form-control" style={{ flex: '1 1 200px' }} placeholder="AA:BB:CC:DD:EE:FF" value={testMac}
              onChange={e => setTestMac(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleTest()} />
            <button className="btn btn-primary" onClick={handleTest} disabled={testLoading}>
              {testLoading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Test →'}
            </button>
          </div>

          {testResult && (
            <div style={{ padding: 16, background: 'var(--bg-hover)', borderRadius: 8, borderLeft: `4px solid ${testResult.decision === 'ACCEPT' ? 'var(--success)' : 'var(--danger)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: '1.5rem' }}>{testResult.decision === 'ACCEPT' ? '✅' : '❌'}</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: testResult.decision === 'ACCEPT' ? 'var(--success)' : 'var(--danger)' }}>
                  {testResult.decision}
                </span>
              </div>
              {[
                ['MAC (display)', testResult.mac],
                ['MAC (hex)', testResult.macHex],
                ['In radcheck', testResult.inRadcheck ? 'Yes' : 'No'],
                ['Radcheck value', testResult.radcheckValue || '—'],
                ['Device found', testResult.deviceFound ? 'Yes' : 'No'],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', gap: 16, fontSize: '0.83rem', marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-muted)', minWidth: 140 }}>{l}</span>
                  <span className="text-mono">{v}</span>
                </div>
              ))}
              {testResult.device && (
                <div style={{ marginTop: 12, padding: '10px', background: 'var(--bg-card)', borderRadius: 6, fontSize: '0.82rem' }}>
                  <div><strong>Student:</strong> {testResult.device.student_name} ({testResult.device.registration_no})</div>
                  <div><strong>Device:</strong> {testResult.device.device_model || 'Unknown model'} — {testResult.device.vendor_name || '—'}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add NAS */}
      {tab === 'nas' && (
        <div className="card" style={{ maxWidth: 600 }}>
          <div className="card-header"><span className="card-title">Add NAS Client</span></div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">NAS IP / Hostname</label>
              <input className="form-control" value={newNas.nasname} placeholder="192.168.1.1"
                onChange={e => setNewNas(f => ({ ...f, nasname: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Short Name</label>
              <input className="form-control" value={newNas.shortname} placeholder="cisco-wlc"
                onChange={e => setNewNas(f => ({ ...f, shortname: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Shared Secret</label>
              <input className="form-control" type="password" value={newNas.secret} placeholder="Min 8 chars"
                onChange={e => setNewNas(f => ({ ...f, secret: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-control" value={newNas.type} onChange={e => setNewNas(f => ({ ...f, type: e.target.value }))}>
                <option value="cisco">Cisco</option><option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-control" value={newNas.description} placeholder="e.g. Main Campus WLC"
              onChange={e => setNewNas(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="alert alert-info" style={{ marginTop: 8 }}>
            ⚠️ The shared secret must match what is configured on your Cisco Wireless Controller under RADIUS server settings.
          </div>
          <button className="btn btn-primary" onClick={() => addNasMut.mutate(newNas)} disabled={addNasMut.isPending}>
            {addNasMut.isPending ? 'Adding…' : '+ Add NAS Client'}
          </button>
        </div>
      )}

      {/* Accounting */}
      {tab === 'accounting' && (
        <div className="card">
          <div className="card-header"><span className="card-title">RADIUS Accounting Sessions</span></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Username</th><th>NAS IP</th><th>Start</th><th>Stop</th><th>Duration (s)</th><th>Input</th><th>Output</th></tr></thead>
              <tbody>
                {acctPage.map(a => (
                  <tr key={a.radacctid}>
                    <td className="text-mono">{a.username}</td>
                    <td className="text-mono">{a.nasipaddress}</td>
                    <td className="text-mono">{a.acctstarttime}</td>
                    <td className="text-mono">{a.acctstoptime || '—'}</td>
                    <td>{a.acctsessiontime || '—'}</td>
                    <td>{a.acctinputoctets ? (a.acctinputoctets/1024).toFixed(1)+' KB' : '—'}</td>
                    <td>{a.acctoutputoctets ? (a.acctoutputoctets/1024).toFixed(1)+' KB' : '—'}</td>
                  </tr>
                ))}
                {(!accounting || accounting.length === 0) && (
                  <tr><td colSpan={7}><div className="empty-state">No accounting records</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={acctP} totalPages={acctTP} onChange={setAcctP} />
        </div>
      )}

      {/* Post-Auth */}
      {tab === 'postauth' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Post-Auth Log (Accept / Reject)</span></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Username (MAC)</th><th>Reply</th><th>Date</th></tr></thead>
              <tbody>
                {postauthPage.map(p => (
                  <tr key={p.id}>
                    <td className="text-mono">{p.username}</td>
                    <td><span className={`badge ${p.reply === 'Access-Accept' ? 'badge-success' : 'badge-danger'}`}>{p.reply}</span></td>
                    <td className="text-mono">{p.authdate}</td>
                  </tr>
                ))}
                {(!postauth || postauth.length === 0) && (
                  <tr><td colSpan={3}><div className="empty-state">No post-auth records</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={postP} totalPages={postTP} onChange={setPostP} />
        </div>
      )}
    </div>
  );
}
